import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import { OffersTable } from "../../../components/dashboard/offers/offers-table";
import { ModuleFilter } from "../../../components/utils/ModuleFilter";
import { getOffersAsync, initialController } from "../../../slices/offer";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { PlusIcon } from "@heroicons/react/24/outline";
import { updateUrlWithException } from "../../../components/Utils";
import { debounce } from "lodash";
import { useSearchParams } from "react-router-dom";
import { getDepartmentsAsync } from "../../../slices/department";
import { getCustomers } from "../../../slices/customer";
import { getContactsAsync } from "../../../slices/customer/contact";
import { getProjectsAsync } from "../../../slices/project";
import { offerStatusOptions } from "../../../components/dashboard/offers/utils";
import OfferSetupPrompts from "../../../components/dashboard/offers/offer-setup-prompts";

const parseFilterValue = (filter) => {
  if (!filter) return [];
  try {
    const parsed = JSON.parse(filter.value);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
};

const getInitialFiltersFromUrl = (searchParams) => {
  const filtersParam = searchParams.get("filters");
  if (filtersParam) {
    try {
      const parsed = JSON.parse(filtersParam);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      console.error("Error parsing offer filters from URL:", e);
    }
  }
  return [];
};

export const OffersList = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const itemState = useSelector((store) => store.offers);
  const departmentState = useSelector((store) => store.departments);
  const customerState = useSelector((store) => store.customers);
  const contactState = useSelector((store) => store.contacts);
  const projectState = useSelector((store) => store.projects);

  const { isLoading } = itemState;
  const [selectedItems, setSelectedItems] = useState([]);

  const [controller, setController] = useState({
    ...initialController,
    filters: getInitialFiltersFromUrl(searchParams),
    page: searchParams.get("page") ? parseInt(searchParams.get("page"), 10) : 1,
    per_page: searchParams.get("per_page")
      ? parseInt(searchParams.get("per_page"), 10)
      : 50,
    query: searchParams.get("query") ? searchParams.get("query") : "",
    sort: searchParams.get("sort") ? searchParams.get("sort") : "DESC",
    sortBy: searchParams.get("sortBy")
      ? searchParams.get("sortBy")
      : "offer_number",
    view: searchParams.get("view") ? searchParams.get("view") : "all",
    fields:
      "id,offer_number,title,name,reference,offer_date,customer,project,status,total_amount,active,contact,our_contact_employee,delivery_date,valid_from,valid_to,currency,subtotal_amount,cost_total,contribution_margin,contribution_margin_percent,current_draft_revision_id,accepted_revision_id,current_draft_revision,accepted_revision,current_draft_revision_number,accepted_revision_number,revision_number,updated_at,offer_feature_flags",
  });

  // Persist controller (including filters) to URL — same pattern as projects/orders
  useEffect(() => {
    updateUrlWithException(
      {
        page: controller.page,
        per_page: controller.per_page,
        query: controller.query,
        sort: controller.sort,
        sortBy: controller.sortBy,
        view: controller.view,
        filters:
          controller.filters && controller.filters.length > 0
            ? JSON.stringify(controller.filters)
            : null,
      },
      "filters"
    );
  }, [controller]);

  useEffect(() => {
    dispatch(
      getDepartmentsAsync({
        page: 1,
        per_page: 100,
        sort: "asc",
        sortBy: "title",
      })
    );
    dispatch(
      getCustomers({
        page: 1,
        per_page: 100,
        sort: "asc",
        sortBy: "name",
      })
    );
    dispatch(
      getContactsAsync({
        page: 1,
        per_page: 100,
        sort: "asc",
        sortBy: "name",
      })
    );
    dispatch(
      getProjectsAsync({
        page: 1,
        per_page: 100,
        sort: "asc",
        sortBy: "name",
        filters: [],
      })
    );
  }, [dispatch]);

  const customerOptions = (customerState.data || []).map((customer) => ({
    value: customer.id.toString(),
    label: customer.displayTitle || customer.name,
  }));

  const contactOptions = (contactState.data || []).map((contact) => ({
    value: contact.id.toString(),
    label: contact.displayTitle || contact.name,
  }));

  const departmentOptions = (departmentState?.data || []).map((department) => ({
    value: department.id.toString(),
    label: department.title,
  }));

  const projectFilterOptions = (projectState.data || []).map((project) => ({
    value: project.id.toString(),
    label: project.displayTitle || project.name || project.project_number,
  }));

  const statusFilterOptions = offerStatusOptions.map((option) => ({
    value: option.value,
    label: t(option.label),
    class: option.class,
  }));

  const buildInitialModuleFilters = () => {
    const filters = Array.isArray(controller.filters) ? controller.filters : [];
    const viewFilter = filters.find((f) => f.name === "view");
    const statusFilter = filters.find((f) => f.name === "status");
    const selectedViews = parseFilterValue(viewFilter);

    const initial = {
      query: controller.query || "",
      active: selectedViews.includes("active"),
      statuses: parseFilterValue(statusFilter),
      customers: [],
      contacts: [],
      departments: [],
      projects: [],
    };

    filters.forEach((filter) => {
      switch (filter.name) {
        case "customer":
          initial.customers.push(String(filter.value));
          break;
        case "contact":
          initial.contacts.push(String(filter.value));
          break;
        case "department":
          initial.departments.push(String(filter.value));
          break;
        case "project":
          initial.projects.push(String(filter.value));
          break;
        default:
          break;
      }
    });

    return initial;
  };

  const handleModuleFilterChange = (filters) => {
    const nextFilters = [];
    const activeOnly = filters.active === true;

    if (activeOnly) {
      nextFilters.push({
        name: "view",
        value: JSON.stringify(["active"]),
      });
    }

    if (filters.statuses && filters.statuses.length > 0) {
      nextFilters.push({
        name: "status",
        value: JSON.stringify(filters.statuses),
      });
    }

    if (filters.customers && filters.customers.length > 0) {
      filters.customers.forEach((customerId) => {
        nextFilters.push({ name: "customer", value: customerId });
      });
    }

    if (filters.contacts && filters.contacts.length > 0) {
      filters.contacts.forEach((contactId) => {
        nextFilters.push({ name: "contact", value: contactId });
      });
    }

    if (filters.departments && filters.departments.length > 0) {
      filters.departments.forEach((departmentId) => {
        nextFilters.push({ name: "department", value: departmentId });
      });
    }

    // ModuleFilter has no built-in project pill; customDropdown key "projects"
    if (filters.projects && filters.projects.length > 0) {
      filters.projects.forEach((projectId) => {
        nextFilters.push({ name: "project", value: projectId });
      });
    }

    setController((prev) => ({
      ...prev,
      page: 1,
      query: filters.query || "",
      view: activeOnly ? "active" : "all",
      filters: nextFilters,
    }));
  };

  const handlePageChange = (newPage) => {
    setController((prev) => ({
      ...prev,
      page: newPage,
    }));
  };

  const debouncedDispatchAction = useMemo(
    () =>
      debounce((nextController) => {
        dispatch(getOffersAsync(nextController));
      }, 250),
    [dispatch]
  );

  useEffect(() => {
    debouncedDispatchAction(controller);
    return () => {
      debouncedDispatchAction.cancel();
    };
  }, [controller, debouncedDispatchAction]);

  const listCount =
    itemState.totalCount != null ? `${itemState.totalCount}` : null;

  const handleImportComingSoon = () => {
    toast(t("offers.list.import_coming_soon"));
  };

  return (
    <>
      <DashboardHeader
        variant="list"
        subtitle={t("offers.list.subtitle")}
        actions={[
          {
            label: t("offers.list.import_quantity_list"),
            action: handleImportComingSoon,
            hidden: false,
            menu: false,
          },
        ]}
        title={t("offers.list.title")}
        listCount={listCount}
        meta={[]}
        mainButton={{
          label: t("offers.list.new"),
          action: null,
          path: "/dashboard/offers/new",
          color: "",
          icon: PlusIcon,
        }}
      >
        <div className="p-5 space-y-4">
          <OfferSetupPrompts />
          <ModuleFilter
            filterCard
            disabled={isLoading}
            onFiltersChange={handleModuleFilterChange}
            initialFilters={buildInitialModuleFilters()}
            searchPlaceholder={t("offers.list.search_placeholder")}
            hideStatusFilter={true}
            selectedItems={selectedItems}
            customerOptions={customerOptions}
            contactOptions={contactOptions}
            departmentOptions={departmentOptions}
            customCheckboxButtonFilters={[
              {
                key: "active",
                label: t("offers.list.active_only"),
                defaultValue: false,
              },
            ]}
            customDropdownFilters={[
              {
                key: "statuses",
                label: t("Status"),
                options: statusFilterOptions,
              },
              {
                key: "projects",
                label: t("Project"),
                options: projectFilterOptions,
              },
            ]}
          />

          <OffersTable
            itemState={itemState}
            itemsCount={itemState.totalCount}
            currentCount={itemState.currentCount}
            page={controller.page}
            onPageChange={handlePageChange}
            perPage={controller.per_page}
            onItemsSelect={setSelectedItems}
            featureFlags={itemState.offerFeatureFlags}
          />
        </div>
      </DashboardHeader>
    </>
  );
};
